import { useState, useEffect } from "react";
import {
  IconButton,
  InputAdornment,
  Modal,
  Typography as T,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FieldHeader, TextInput } from "../../../../components/General";
import Button from "../../../../components/Button";
import {
  LoginContainer,
  LoginButton,
  LoginGradient,
  LoginHeader,
  LoginLogo,
  LoginNav,
  InnerNavContainer,
  PlainButton,
} from "../../components";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import loginIcon from "../../../../assets/logos/login.svg";
import loginMobileIcon from "../../../../assets/logos/loginMobile.svg";
import useLogin from "../../../../hooks/mutations/useLogin";
import { employeeAccountStatusPath } from "../../../../routes/paths";
import AuthService from "../../../../api/services/AuthService";
import { LinkConstants } from "../../../../helpers/links";

const ModalContainer = styled("div")(`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 500px;
  background-color: #FDFDFD;
  border-radius: 5px;
  padding: 16px 32px;
  margin: 8px;
`);

const SignUpMsgModal = ({
  isOpen,
  closeModal,
}: {
  isOpen: boolean;
  closeModal: () => void;
}) => {
  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ModalContainer>
        <div>
          <T variant="lgMed">Sign Up with Employer Link</T>
          <T variant="mdReg">
            To sign up, please use the link provided by your employer.
          </T>
        </div>
        <Button onClick={closeModal}>Close</Button>
      </ModalContainer>
    </Modal>
  );
};

const EmployeeLogin = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<any>(null);
  const [signUpModalOpen, setSignUpModalOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));

  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      navigate(employeeAccountStatusPath);
    }
  }, [navigate]);

  // Mutations
  const { mutate: loginFn } = useLogin({
    onSuccessHandler: () => navigate(employeeAccountStatusPath),
  });

  const handleLogin = async () => {
    if (!email || !password) {
      toast.error("Please fill out all fields");
      return;
    }

    loginFn({ email, password });
  };

  return (
    <LoginContainer>
      {isMobile && (
        <LoginGradient>
          <LoginLogo src={loginMobileIcon} alt="logo" />
        </LoginGradient>
      )}
      <LoginNav>
        <InnerNavContainer>
          <LoginHeader>Login</LoginHeader>
          <FieldHeader sx={{ margin: "40px 0 0" }}>Email</FieldHeader>
          <TextInput
            id="email-field"
            size="small"
            placeholder="Work Email"
            value={email}
            focused
            onChange={(event) => setEmail(event.target.value)}
          />
          <FieldHeader>Password</FieldHeader>
          <TextInput
            id="password-field"
            placeholder="Password"
            size="small"
            type={showPassword ? "text" : "password"}
            focused
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    size={"small"}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <LoginButton children={"Continue"} onClick={handleLogin} />
          <PlainButton
            children={"Forgot password?"}
            onClick={() => window.open(LinkConstants.passwordReset, "_blank")}
            sx={{ marginTop: "16px" }}
          />
          <PlainButton
            children={"Sign up"}
            onClick={() => setSignUpModalOpen(true)}
          />
        </InnerNavContainer>
      </LoginNav>
      <SignUpMsgModal
        isOpen={signUpModalOpen}
        closeModal={() => setSignUpModalOpen(false)}
      />
      {!isMobile && (
        <LoginGradient>
          <LoginLogo src={loginIcon} alt="logo" />
        </LoginGradient>
      )}
    </LoginContainer>
  );
};

export default EmployeeLogin;
